// src/pages/admin/AdminCockpit.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  VStack,
  useToast,
  Flex,
  Spinner,
  Text,
} from "@chakra-ui/react";

import { useAuth } from "../../context/useAuth";

// Existing modules
import DispatchReadySectors from "../../components/DispatchReadySectors";
import PrintScheduler from "../../components/PrintScheduler";
import DispatchHistory from "../../components/DispatchHistory";
import ReferralAudit from "../../components/ReferralAudit";
import DebugOverlay from "../../components/DebugOverlay";

// NEW admin modules
import RenewalDashboard from "../../components/admin/RenewalDashboard";
import FlyerSlotsAdmin from "../../components/admin/FlyerSlotsAdmin";

// BRAND
import BrandLogo from "../../components/BrandLogo";

export default function AdminCockpit() {
  const { user, profile, loading, error } = useAuth();
  const businessId = profile?.business_uuid;
  const toast = useToast();
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (user && profile?.user_role !== "admin") {
      setAccessDenied(true);
      toast({
        title: "Access Denied",
        description: "This cockpit is for internal use only.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [user, profile, toast]);

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
        <Spinner size="xl" />
        <Text fontSize="md" color="gray.500">
          Loading admin cockpit…
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
        <Text fontSize="lg" color="red.500">
          {error.message || "Failed to load admin cockpit"}
        </Text>
      </Flex>
    );
  }

  if (accessDenied) {
    return (
      <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
        <Text fontSize="lg" color="red.500">
          Access denied — admin role required.
        </Text>
      </Flex>
    );
  }

  return (
    <Box p={8} bg="gray.50" minH="100vh">
      {/* BRAND HEADER */}
      <Flex justify="center" mb={8}>
        <BrandLogo size={100} />
      </Flex>

      <Heading size="lg" mb={6} textAlign="center" color="red.600">
        🛠️ Admin Cockpit
      </Heading>

      <VStack align="stretch" spacing={8}>
        {/* 🔥 NEW: Renewal Intelligence */}
        <RenewalDashboard />
        <FlyerSlotsAdmin />

        {/* 🔥 Existing Operational Modules */}
        <DispatchReadySectors
          businessId={businessId}
          campaignId={null}
          region={null}
          group={null}
        />

        <PrintScheduler businessId={businessId} />
        <DispatchHistory businessId={businessId} />
        <ReferralAudit businessId={businessId} />
        <DebugOverlay businessId={businessId} />
      </VStack>
    </Box>
  );
}
